import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation Cover
    FaTools,            // For Engine and Gearbox Protection
    FaFileInvoiceDollar,// For Return to Invoice
    FaTruckPickup,      // For Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaCarSide,          // For Tyre and Rim Protection
    FaKey,              // For Key Replacement Cover
    FaBatteryFull,      // For Hybrid Battery Cover
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const LexusCarInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is Lexus insurance more expensive than Audi or BMW?",
      answer: "Premiums are similar across brands but may vary based on model and location.",
    },
    {
      question: "Do Lexus hybrids require special insurance?",
      answer: "Yes, it's best to include battery and hybrid component coverage.",
    },
    {
      question: "What is zero depreciation cover?",
      answer: "It ensures you get full value of replaced parts without depreciation deductions.",
    },
    {
      question: "Can I buy Lexus insurance online?",
      answer: "Absolutely. Leading insurers allow complete digital purchase and renewal.",
    },
    {
      question: "What happens if my Lexus is declared a total loss?",
      answer: "Return to Invoice cover ensures you get the original price back.",
    },
    {
      question: "Is Roadside Assistance useful for Lexus owners?",
      answer: "Yes, especially for intercity travel or high-mileage users.",
    },
    {
      question: "Can I insure my Lexus through a dealership?",
      answer: "Dealerships offer bundled plans but may not always be the most cost-effective.",
    },
    {
      question: "Are hybrid batteries covered by insurance?",
      answer: "Only if you opt for specific hybrid or battery-related add-ons.",
    },
    {
      question: "Is NCB applicable when switching policies?",
      answer: "Yes, No Claim Bonus is transferable across insurers.",
    },
    {
      question: "Are modifications allowed under insurance?",
      answer: "Minor modifications are fine, but always declare them to your insurer.",
    },
    {
      question: "Does Lexus India offer in-house insurance?",
      answer: "They often partner with insurers but do not issue policies directly.",
    },
    {
      question: "Can I get insurance for a used Lexus?",
      answer: "Yes, subject to inspection and policy approval.",
    },
    {
      question: "What add-on is most important for a hybrid Lexus?",
      answer: "Battery cover and zero depreciation are top priorities.",
    },
    {
      question: "Are Lexus tyres covered under standard insurance?",
      answer: "No, tyre protection must be added separately.",
    },
    {
      question: "Can I bundle multiple Lexus cars under one policy?",
      answer: "Some insurers offer fleet or multi-car policy benefits.",
    },
    {
      question: "Is engine protection really necessary?",
      answer: "Yes, especially for hybrid engines with dual systems.",
    },
    {
      question: "Do insurers cover Lexus EV charging units?",
      answer: "Yes, if declared during policy purchase.",
    },
    {
      question: "What are common exclusions in Lexus car insurance?",
      answer: "Drunk driving, illegal racing, and undeclared modifications.",
    },
    {
      question: "Do I need to renew my Lexus insurance annually?",
      answer: "You can opt for 1-year or long-term policies (up to 3 years).",
    },
    {
      question: "Can I reduce premium by increasing deductibles?",
      answer: "Yes, but ensure you can afford the cost in case of a claim.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "Ensures full claim on part replacements.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description: "Recovers the invoice amount if the car is declared a total loss.",
      icon: <FaFileInvoiceDollar className="text-4xl text-orange-600 mb-4" />,
    },
    {
      title: "Hybrid Battery Cover",
      description: "Covers battery degradation or damage.",
      icon: <FaBatteryFull className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Engine and Gearbox Protection",
      description: "Safeguards complex powertrains and e-motors.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Tyre and Rim Protection",
      description: "Useful for low-profile premium tyres.",
      icon: <FaCarSide className="text-4xl text-indigo-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "For highway travel, breakdowns, or emergencies.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Covers coolant, engine oil, and other minor items during repairs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Key Replacement Cover",
      description: "Lexus smart keys can be expensive to replace.",
      icon: <FaKey className="text-4xl text-purple-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];
const lexusinsurance = [
    {
      name: "ES",
      path: "/lexus-es-car-insurance", 
      image: "/assets/lexuses.jpeg",
    },
    {
      name: "NX",
      path: "/lexus-nx-car-insurance", 
      image: "/assets/lexusnx.jpeg",
    },
    {
      name: "RX",
      path: "/lexus-rx-car-insurance", 
      image: "/assets/lexusrx.jpeg",
    },
     {
      name: "LX",
      path: "/lexus-lx-car-insurance", 
      image: "/assets/lexuslx.jpeg",
    },
    {
      name: "LM",
      path: "/lexus-lm-car-insurance", 
      image: "/assets/lexuslm.jpeg",
    },
    {
      name: "LC",
      path: "/lexus-lc-car-insurance", 
      image: "/assets/lexuslc.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>Lexus Car Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Lexus car insurance options in India. Compare quotes, customize coverage, and buy online to protect your Lexus vehicle."
        />
        <meta
          name="keywords"
          content="lexus car insurance, luxury car insurance india, lexus es insurance, lexus rx insurance, lexus ls insurance, car insurance quotes"
        />
         <link
          rel="canonical"
          href="https://thefinmaster.com/lexus-car-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />

      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Lexus Car Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Lexus Car Insurance </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Lexus is known for its blend of luxury, craftsmanship, and innovative technology. Whether you own an elegant ES or a powerful RX, protecting your vehicle starts with the right insurance plan. This guide takes you through everything you need to know about insuring a Lexus, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/linsurance.jpeg"
                alt="Lexus Car"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Lexus in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Lexus entered the Indian market with a promise of sustainable luxury. The brand blends premium interiors, hybrid powertrains, and sharp design language to cater to the modern Indian customer. With a focus on comfort, innovation, and silent performance, Lexus is preferred by entrepreneurs, executives, and HNIs who seek exclusivity without loud branding.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Popular Lexus models in India include the ES, RX, NX, LX, and LS series. The majority of Lexus vehicles sold in India are hybrids, which combine performance with fuel efficiency. As a result, the insurance policy must cover both conventional and hybrid components for full protection.
          </p>
        </div>
      </div>
        <div className="p-4 sm:p-8 bg-white">
                                <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                  Choose Your Car Brand
                                </h2>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                  {lexusinsurance.map((calc, index) => (
                                    <motion.div
                                      key={index}
                                      whileHover={{ scale: 1.04 }}
                                      initial={{ opacity: 0, y: 20 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ duration: 0.3, delay: index * 0.05 }}
                                    >
                                      <Link
                                        to={calc.path}
                                        className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                      >
                                        <img
                                          src={calc.image}
                                          alt={calc.name}
                                          className="w-16 h-16 object-contain mb-2"
                                        />
                                        <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                          {calc.name}
                                        </span>
                                      </Link>
                                    </motion.div>
                                  ))}
                                </div>
                              </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Lexus Owners Need Specialized Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Lexus vehicles are engineered with cutting-edge driver-assistance systems, sophisticated infotainment setups, and hybrid drivetrains. These technologies offer great benefits to users but come with higher maintenance and part replacement costs. Here’s why you need comprehensive insurance for your Lexus:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Protection from road accidents, theft, floods, and fire</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Smooth access to authorized Lexus service centers</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Availability of genuine hybrid-specific components</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Financial protection for high-cost repairs</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Legal liability coverage for third-party damages or injuries</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A thoughtfully selected insurance plan enhances the ownership experience and protects the investment.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Lexus Models and Estimated Insurance Premiums
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s an overview of popular Lexus models in India, their approximate Insured Declared Values (IDV), and average insurance premiums:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "ES 300h", category: "Executive Sedan", idv: "65–80 Lakh", premium: "₹48,000 to ₹65,000" },
              { model: "RX 500h / 350h", category: "Mid-size Luxury SUV", idv: "90 Lakh–1.3 Cr", premium: "₹60,000 to ₹85,000" },
              { model: "NX 350h", category: "Compact SUV", idv: "70–85 Lakh", premium: "₹50,000 to ₹70,000" },
              { model: "LX 500d", category: "Full-size SUV", idv: "2.5 Cr–2.8 Cr+", premium: "₹1.2 Lakh to ₹1.8 Lakh" },
              { model: "LS 500h", category: "Flagship Sedan", idv: "2 Cr–2.5 Cr", premium: "₹1 Lakh to ₹1.6 Lakh" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">Category</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["ES 300h", "Executive Sedan", "65–80 Lakh", "₹48,000 to ₹65,000"],
                  ["RX 500h / 350h", "Mid-size Luxury SUV", "90 Lakh–1.3 Cr", "₹60,000 to ₹85,000"],
                  ["NX 350h", "Compact SUV", "70–85 Lakh", "₹50,000 to ₹70,000"],
                  ["LX 500d", "Full-size SUV", "2.5 Cr–2.8 Cr+", "₹1.2 Lakh to ₹1.8 Lakh"],
                  ["LS 500h", "Flagship Sedan", "2 Cr–2.5 Cr", "₹1 Lakh to ₹1.6 Lakh"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for Lexus Car Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Given the hybrid systems and premium features in Lexus cars, certain add-ons can be highly beneficial:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Insurers for Lexus Car Insurance in India
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Lexus owners benefit from choosing insurers with a strong track record in luxury car support:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Tata AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Popular among premium car buyers for fast claim processing.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ICICI Lombard</h3>
            <p className="text-gray-700 text-sm mt-1">Offers customizable policies for hybrid vehicles.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">HDFC ERGO</h3>
            <p className="text-gray-700 text-sm mt-1">Extensive network with hybrid-aware garages.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Reliance General</h3>
            <p className="text-gray-700 text-sm mt-1">Known for flexibility in add-ons and hybrid coverage.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">Digital-first approach with dynamic pricing.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Save on Lexus Insurance Premium
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Even premium insurance plans can be optimized using these methods:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare policies across 3–5 insurers for best rates.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Retain your No Claim Bonus (NCB) by avoiding minor claims.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Consider voluntary deductibles to lower premiums.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install approved anti-theft and GPS tracking devices.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Explore multi-year policy discounts.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Lexus Car Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              You’ll typically need the following documents to buy or renew Lexus car insurance:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Copy of your car’s Registration Certificate (RC)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Existing policy (for renewals)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Identity proof – Aadhaar or PAN card</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Passport-size photograph (for offline channels)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Lexus Car Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Lexus cars symbolize elegance and innovation, and they deserve insurance policies that reflect the same values. Whether you drive the hybrid NX or the luxury flagship LS, investing in a tailored insurance plan helps ensure a hassle-free ownership experience.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we guide you through plan comparisons, help you pick the right insurer, and assist in customizing policies that align with your car model, driving habits, and long-term goals.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default LexusCarInsurance;